import { Alert, Button, Col, message, Row, Space, UploadProps } from "antd";
import Dragger from "antd/es/upload/Dragger";
import { UploadFile } from "antd/es/upload/interface";
import { useState } from "react";
import Icon from "../../assets/icons/icon";
import _t from "../../lang/translate";
import { OpenModalProps } from "../../types/modalTypes";
import assertFC from "../../utilities/assertFC";
import { renderErrorMessage } from "../messages/errorMessage";
import { Typography } from "antd/es";
import moment from 'moment';

export type CSVField = {
  vinOrLink?: string;
  mileage?: string;
  firstRegDate?: string;
  taxType?: string;
}

/** @param date - format dd/mm/yyyy --> 31/12/2020 */
function parseDate(date?: string) {
  const d = moment(date, "DD/MM/YYYY"); // strict mode
  if (!d.isValid()) {
    throw new Error(`Ugyldig datoformat. Forventet DD/MM/YYYY, men fik "${date}".`);
  }

  if (d.isAfter(moment(), "day")) {
    throw new Error(
      `Registreringsdato må ikke være i fremtiden. "${d.format("DD/MM/YYYY")}" blev angivet, men seneste tilladte dato er "${moment().format("DD/MM/YYYY")}".`
    );
  }
  return d.toISOString()
}

export default function TaxCaseBulkCSVUpload<T = CSVField[]>({
  onSuccess,
  onCancel,
}: OpenModalProps<T>) {
  const [files, setFiles] = useState<UploadFile[]>([])
  const [csvValues, setCsvValues] = useState<CSVField[] | null>(null)

  async function readFile(file: UploadFile) {
    if (!file.originFileObj) {
      throw new Error("No file selected")
    }
    try {
      const csvFile = file.originFileObj as File;

      const text = await csvFile.text()
      const separator = detectCsvDelimiterByTotal(text)

      const rows = text
        .trim()
        .split("\n")
        .map((line) => line.split(separator ?? ',').map((cell) => cell.trim()));
      const [header, ...rest] = rows
      const data = rest.map<CSVField>(item => {
        return {
          vinOrLink: item[0],
          mileage: item[1],
          firstRegDate: parseDate(item[2]),
          taxType: item[3],
        }
      })
      return data
    } catch (error) {
      renderErrorMessage(error, "", 8)
      return null
    }
  }

  const handleConfirm = async () => {
    try {
      if (csvValues) {
        onSuccess(csvValues as T);
      } else {
        onCancel()
      }
    } catch (error) {
      renderErrorMessage(error)
    }
  };

  const props: UploadProps = {
    name: 'file',
    multiple: false,
    maxCount: 1,
    accept: ".csv",
    fileList: files,
    showUploadList: true,
    async onChange({ fileList, file }) {
      const { status } = file
      const fixedFileList = fileList.map(file => {
        if (file.originFileObj) {
          file.url = URL.createObjectURL(file.originFileObj)
        }
        return file
      })
      setFiles(fixedFileList)
      const csvData = await readFile(fixedFileList[0])
      setCsvValues(csvData)
      if (status === 'error') {
        message.error(`${file.name} file upload failed.`);
      }
    },
    beforeUpload: () => false,
  };

  return (
    <div>
      <h2 className="modal-title">{_t("massCreation")}</h2>

      <div style={{ position: "relative" }}>
        <Dragger {...props} >
          <p className="ant-upload-drag-icon">
            <Icon name="file-tray-outline" />
          </p>
          <p className="ant-upload-text">{_t("upload_csv_file_with_vehicles")}</p>
          <p className="ant-upload-hint">
            {_t("only_one_file_possible")}
          </p>
        </Dragger>
      </div>
      <Typography.Paragraph type="secondary" className="mt-03">
        CSV-filen skal indeholde kolonner for: Stelnummer/link, kilometerstand, 1. reg.dato
      </Typography.Paragraph>
      <Alert type="info" showIcon
        message={
          <Row justify="space-between">
            <Col>
              <span className="text-bold text-primary">CSV format eksempel</span>
            </Col>
            <Col>
              <Button className="text-normal" type="primary" size="small">
                <a href={"/masseoprettelse-template.csv"} download>
                  {_t("template")}
                </a>
              </Button>
            </Col>

          </Row>
        }
        description={
          <code className="text-tiny">
            stelnummer_eller_link,kilometer,regdato,afgiftstype <br />
            WAUZZZ8ZXLR123456,120000,2022-05-22,personbil <br />
            https://www.bilbasen.dk/...,25000,2023-11-10,van
          </code>
        } />
      <div className="text-right mt-1">
        <Space>
          <Button type="ghost" onClick={onCancel}>
            {_t("cancel")}
          </Button>
          <Button disabled={!csvValues} onClick={handleConfirm} type="primary">
            {_t("start", "creation")}
            {csvValues && `(${csvValues.length})`}
          </Button>
        </Space>
      </div>
    </div>
  );
}

assertFC(TaxCaseBulkCSVUpload);

/**
 * Courtesy of ChatGPT
 *
 * Detects the most likely CSV delimiter by counting occurrences across all lines,
 * while ignoring delimiters that appear inside quoted fields.
 *
 * @param {string} csvContent - The raw CSV file content.
 * @param {string[]} [possibleDelimiters=[",", ";", "\t", "|"]] - Delimiters to check.
 * @returns {string|null} - The detected delimiter or null if none found.
 */
function detectCsvDelimiterByTotal(csvContent: string, possibleDelimiters: string[] = [",", ";", "\t", "|"]): string | null {
  const lines = csvContent.split(/\r?\n/).filter(Boolean);
  if (lines.length === 0) return null;

  const totals: Record<string, number> = {};
  for (const delimiter of possibleDelimiters) {
    totals[delimiter] = 0;

    for (const line of lines) {
      let inQuotes = false;
      let count = 0;

      for (let i = 0; i < line.length; i++) {
        const char = line[i];

        if (char === '"') {
          // Toggle quote state, unless it's an escaped double-quote ("")
          if (inQuotes && line[i + 1] === '"') {
            i++; // skip escaped quote
          } else {
            inQuotes = !inQuotes;
          }
        } else if (!inQuotes && char === delimiter) {
          count++;
        }
      }

      totals[delimiter] += count;
    }
  }

  // Pick the delimiter with the highest count
  let bestDelimiter = null;
  let bestCount = -1;
  for (const [delimiter, count] of Object.entries(totals)) {
    if (count > bestCount) {
      bestCount = count;
      bestDelimiter = delimiter;
    }
  }

  return bestDelimiter;
}
